import { useEffect, useRef, useState } from "react";
import { Routes, Route } from "react-router-dom";
import AuthPage from "../pages/AuthPage";
import MainPage from "../pages/HomePage";
import PageNotFound from "../pages/PageNotFound";
import { useAuthGuard } from "../features/auth/useAuthGuard";
import AppPreloader from "../shared/components/AppPreloader/AppPreloader";
import {
  useAuthStore,
  useServersStore,
  useProjectsStore,
} from "../shared/store";
import { preload } from "react-dom";
import {
  background,
  almiOnSofa,
  apocalypse,
  winter,
  carpet,
  lastSupper,
  diplodocus,
  statham,
  floor,
} from "../assets/images";
import ProjectsSearchPage from "../pages/ProjectsSearchPage";
import ServersSearchPage from "../pages/ServersSearchPage";
import ServerStatusPage from "../pages/ServerStatusPage";
import ProjectStatusPage from "../pages/ProjectStatusPage";
import { verifyAuthToken } from "../shared/utils/api";

const App = () => {
  const [isInitializing, setIsInitializing] = useState(true);

  const token = useAuthStore((s) => s.token);
  const prevTokenRef = useRef<string | null>(null);

  useEffect(() => {
    const initApp = async () => {
      try {
        useAuthStore.getState().loadFromStorage();

        const currentToken = useAuthStore.getState().token;

        if (currentToken) {
          const isValid = await verifyAuthToken();

          if (!isValid) {
            localStorage.removeItem("auth_token");
            useAuthStore.getState().loadFromStorage();
          } else {
            await Promise.all([
              useServersStore.getState().loadServers(),
              useProjectsStore.getState().fetchProjects(),
            ]);
          }
        }
      } catch (err) {
        console.error("Init error:", err);
      } finally {
        setTimeout(() => setIsInitializing(false), 600);
      }

      const imagesToPreload = [
        background,
        almiOnSofa,
        apocalypse,
        winter,
        carpet,
        lastSupper,
        diplodocus,
        statham,
        floor,
      ];
      imagesToPreload.forEach((src) => {
        preload(src, { as: "image" });
        new Image().src = src;
      });
    };

    initApp();
  }, []);

  useEffect(() => {
    if (token && prevTokenRef.current === null && !isInitializing) {
      const loadDataAfterLogin = async () => {
        try {
          await Promise.all([
            useServersStore.getState().loadServers(),
            useProjectsStore.getState().fetchProjects(),
          ]);
        } catch (err) {
          console.error("Post-login data load error:", err);
        }
      };

      loadDataAfterLogin();
    }

    prevTokenRef.current = token;
  }, [token, isInitializing]);

  useAuthGuard();

  if (isInitializing) {
    return <AppPreloader />;
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<MainPage />} />
        <Route path="/login" element={<AuthPage />} />
        <Route path="/servers" element={<ServersSearchPage />} />
        <Route path="/projects" element={<ProjectsSearchPage />} />
        <Route path="/status/:net" element={<ServerStatusPage />} />
        <Route path="/project/:net" element={<ProjectStatusPage />} />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </>
  );
};

export default App;